import {
  Button,
  FormControl,
  Grid2,
  IconButton,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { CommonPagination, GKSelect, GKTable } from "components";
import {
  ConstituentScreeningIcon,
  DeleteIcon,
  ReloadIcon,
} from "components/Icons";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { CgMathPlus } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { BILLING_STATUS } from "utils/constants";
import { extraColumnsField, moneyCommaSeparatorPoint } from "utils/helpers";
import CreateAdvisorModal from "./CreateAdvisorModal";

const AdvisorTable = () => {
  const navigate = useNavigate();

  const {
    state: { commonFilter },
  } = useAppContext();

  const [advisorCreateModal, setAdvisorCreateModal] = useState(false);

  const requestBody = {
    requireTotalCount: true,
    take: commonFilter?.advisor?.take,
    skip: commonFilter?.advisor?.skip,
    sort: JSON.stringify(
      commonFilter?.advisor?.sortModel?.map((data: any) => {
        return {
          selector: data.field,
          desc: data.sort !== "asc",
        };
      })
    ),
    search: commonFilter?.advisor?.search || "",
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["DASHBOARD_ADVISOR_TABLE", requestBody],
    queryFn: () =>
      axiosInstance.get(`/super_admin/advisors/`, {
        params: requestBody,
      }),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: refreshAccount } = useMutation({
    mutationKey: ["REFRESH_ACCOUNT"],
    mutationFn: (id: number | string) =>
      axiosInstance.get(`/super_admin/${id}/refresh_account/`),
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Refresh Account Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: deleteClient } = useMutation({
    mutationKey: ["DELETE_CLIENT"],
    mutationFn: (data) =>
      axiosInstance.delete(`/super_admin/${data}/delete-advisor/`),
    onSuccess: () => {
      refetch();
    },
  });

  const { mutate: handleUpdateStatus } = useMutation({
    mutationKey: ["UPDATE_STATUS"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/super_admin/${data.phone}/update_payment_status/`, {
        status: data.status,
      }),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Status updated successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Company Name", "companyName", 200, 1),
      valueGetter: (row) => row?.row?.companyName || "-",
    },
    {
      ...extraColumnsField("Advisor Name", "name", 170, 1),
      valueGetter: (row) => row?.row?.name || "-",
    },
    {
      ...extraColumnsField("Phone Number", "phone", 150, 1),
      type: "number",
      valueGetter: (row) => row?.row?.phone || "-",
    },
    {
      ...extraColumnsField("Total Aum", "totalAum", 100, 1),
      type: "number",
      valueGetter: (row) => row?.row?.totalAum || 0,
      valueFormatter: (params) => moneyCommaSeparatorPoint(params.value),
    },
    {
      ...extraColumnsField("Created on", "createdOn", 120, 1),
      // valueFormatter: (params) => dayjs(params?.value).format("DD MMM YYYY"),
      valueGetter: (row) => row?.row?.createdOn || "N/A",
    },
    {
      ...extraColumnsField("Last Activity", "lastActivity", 120, 1),
      // valueGetter: (row) => row?.row?.lastActivity || "N/A",
      valueFormatter: (params) =>
        params?.value ? dayjs(params.value).format("DD MMM YYYY") : "N/A",
    },
    {
      ...extraColumnsField("Last Login Date", "lastLoginDate", 130, 1),
      valueFormatter: (params) =>
        params?.value ? dayjs(params.value).format("DD MMM YYYY") : "N/A",
      valueGetter: (row) => row?.row?.lastLoginDate || null,
    },
    {
      ...extraColumnsField("Payment Status", "paymentStatus", 140, 1),
      renderCell: (item) => {
        const selectedValue = item?.row?.paymentStatus || "";
        const phone = item?.row?.phone;

        return (
          <FormControl variant="standard" fullWidth>
            <GKSelect
              size="small"
              name="paymentStatus"
              value={selectedValue}
              onChange={(e) => {
                handleUpdateStatus({
                  status: e.target.value,
                  phone: phone,
                });
              }}
            >
              {BILLING_STATUS.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        );
      },
    },
    {
      ...extraColumnsField("Clients", "clients", 100, 1),
      type: "number",
      valueGetter: (row) => row?.row?.clients || 0,
    },
    {
      ...extraColumnsField("Portfolios", "portfolios", 100, 1),
      type: "number",
      valueGetter: (row) => row?.row?.portfolios || 0,
    },
    {
      ...extraColumnsField("Actions", "action", 150, 1),
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Grid2 container>
            <Tooltip arrow placement="top" title="Refresh the account">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  refreshAccount(params.id);
                }}
              >
                <ReloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="top" title="View Details">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/advisor/${params.row.id}/details`);
                }}
              >
                <ConstituentScreeningIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="top" title="Delete Advisor">
              <IconButton
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  deleteClient(params?.row?.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid2>
        );
      },
    },
  ];

  const cusPagination = () => {
    return (
      <CommonPagination
        name="advisor"
        totalCount={data?.data.totalCount || 0}
      />
    );
  };

  return (
    <>
      <GKTable
        loading={isFetching}
        columns={columns}
        rows={data?.data?.data || []}
        name="advisor"
        CustomPagination={!isFetching && cusPagination}
        onRowClick={(params) => navigate(`/app/preference/${params.row.phone}`)}
        headerComponent={
          <Button
            onClick={() => setAdvisorCreateModal(true)}
            variant="contained"
            size="medium"
          >
            <CgMathPlus size={18} /> Create Advisor
          </Button>
        }
      />

      {advisorCreateModal && (
        <CreateAdvisorModal
          open={advisorCreateModal}
          setOpen={setAdvisorCreateModal}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default AdvisorTable;
